import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const BackupCodesDisplay = ({ codes, onConfirm }) => {
  const [confirmed, setConfirmed] = useState(false);

  const handleDownload = () => {
    const content = `MyBodyToday - Two-Factor Authentication Backup Codes\n\nGenerated: ${new Date()?.toLocaleString()}\n\nIMPORTANT: Store these codes in a secure location. Each code can only be used once.\n\n${codes?.map((code, idx) => `${idx + 1}. ${code}`)?.join('\n')}\n\nIf you lose access to your authentication method, you can use these codes to sign in.`;
    
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mybodytoday-backup-codes.txt';
    a?.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '', 'width=600,height=400');
    printWindow?.document?.write(`
      <html>
        <head>
          <title>MyBodyToday - Backup Codes</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { font-size: 18px; margin-bottom: 10px; }
            .warning { background: #fff3cd; border: 1px solid #ffc107; padding: 10px; margin: 10px 0; }
            .codes { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin: 20px 0; }
            .code { font-family: monospace; font-size: 14px; padding: 5px; border: 1px solid #ddd; }
          </style>
        </head>
        <body>
          <h1>MyBodyToday - Two-Factor Authentication Backup Codes</h1>
          <p>Generated: ${new Date()?.toLocaleString()}</p>
          <div class="warning">
            <strong>IMPORTANT:</strong> Store these codes in a secure location. Each code can only be used once.
          </div>
          <div class="codes">
            ${codes?.map((code, idx) => `<div class="code">${idx + 1}. ${code}</div>`)?.join('')}
          </div>
          <p><small>If you lose access to your authentication method, you can use these codes to sign in.</small></p>
        </body>
      </html>
    `);
    printWindow?.document?.close();
    printWindow?.print();
  };

  const handleCopy = () => {
    const text = codes?.join('\n');
    navigator?.clipboard?.writeText(text);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold text-foreground mb-2">Save Your Backup Codes</h2>
        <p className="text-muted-foreground">Store these codes securely - you'll need them if you lose access to your authentication method</p>
      </div>

      {/* Warning */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Icon name="AlertTriangle" size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="text-sm font-semibold text-amber-900 mb-1">Critical: Save These Codes Now</h3>
            <ul className="text-xs text-amber-800 space-y-1">
              <li>• Each code can only be used once</li>
              <li>• You won't be able to see these codes again</li>
              <li>• Store them in a secure location (password manager, safe, etc.)</li>
              <li>• Don't share these codes with anyone</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Backup Codes */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="grid grid-cols-2 gap-3 mb-4">
          {codes?.map((code, idx) => (
            <div
              key={idx}
              className="bg-muted rounded px-3 py-2 font-mono text-sm text-center"
            >
              {code}
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
            iconName="Download"
          >
            Download
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handlePrint}
            iconName="Printer"
          >
            Print
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            iconName="Copy"
          >
            Copy
          </Button>
        </div>
      </div>

      {/* Storage Recommendations */}
      <div className="bg-card border border-border rounded-lg p-6">
        <h3 className="font-semibold text-foreground mb-3">Recommended Storage Methods</h3>
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <Icon name="Lock" size={20} className="text-primary mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-foreground">Password Manager</h4>
              <p className="text-xs text-muted-foreground">Store in a secure note in your password manager</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Icon name="FileText" size={20} className="text-primary mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-foreground">Physical Storage</h4>
              <p className="text-xs text-muted-foreground">Print and keep in a safe or locked drawer</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Icon name="Cloud" size={20} className="text-primary mt-0.5" />
            <div>
              <h4 className="text-sm font-medium text-foreground">Encrypted Cloud Storage</h4>
              <p className="text-xs text-muted-foreground">Save in an encrypted cloud storage service</p>
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation */}
      <div className="bg-card border border-border rounded-lg p-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e?.target?.checked)}
            className="mt-1"
          />
          <span className="text-sm text-foreground">
            I have saved these backup codes in a secure location and understand I won't be able to see them again.
          </span>
        </label>
      </div>

      {/* Actions */}
      <Button
        onClick={onConfirm}
        disabled={!confirmed}
        className="w-full"
        iconName="ArrowRight"
        iconPosition="right"
      >
        Continue to Verification Test
      </Button>
    </div>
  );
};

export default BackupCodesDisplay;